/**
 * LoadingSpinner.jsx - Loading State Components
 * 
 * @description
 * Provides various loading indicators for different use cases
 * throughout the application.
 * 
 * @exports
 * - LoadingSpinner: Animated spinner component
 * - LoadingButton: Button with loading state
 * - LoadingPage: Full-page loading overlay
 * 
 * @components
 * 
 * 1. LoadingSpinner
 *    @props
 *    - size: 'sm' | 'md' | 'lg' (default: 'md')
 *    - className: Additional CSS classes
 *    @usage
 *    ```jsx
 *    <LoadingSpinner size="lg" />
 *    ```
 * 
 * 2. LoadingButton
 *    @props
 *    - loading: boolean - Shows spinner when true
 *    - children: React node - Button content
 *    - className: Additional CSS classes
 *    - ...props: Other button props (onClick, type, etc.)
 *    @usage
 *    ```jsx
 *    <LoadingButton loading={isSubmitting} onClick={handleSubmit}>
 *      Save Changes
 *    </LoadingButton>
 *    ```
 * 
 * 3. LoadingPage
 *    @props
 *    - message: string - Loading message (default: 'Loading...')
 *    @usage
 *    ```jsx
 *    if (isLoading) return <LoadingPage message="Fetching data..." />;
 *    ```
 * 
 * @sizes
 * - sm: 16x16px (w-4 h-4)
 * - md: 20x20px (w-5 h-5) - default
 * - lg: 24x24px (w-6 h-6)
 * 
 * @styling
 * - Animated spinning circle
 * - Purple gradient colors
 * - Smooth rotation animation
 * - Disabled state for buttons
 * 
 * @animations
 * - Continuous spin rotation
 * - Fade transitions
 * - Button state transitions
 * 
 * @accessibility
 * - Buttons disabled during loading
 * - Screen reader friendly
 * - Preserves button text during loading
 * 
 * @useCases
 * - Form submissions
 * - Data fetching
 * - File uploads
 * - Page transitions
 * - Async operations
 */

const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div
        className={`animate-spin rounded-full border-b-2 border-purple-600 ${sizeClasses[size]}`}
        style={{
          borderTopColor: 'transparent',
          borderRightColor: 'transparent',
          borderLeftColor: 'transparent',
        }}
      />
    </div>
  );
};

export const LoadingPage = ({ message = 'Loading...' }) => {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center">
      <LoadingSpinner size="xl" />
      <p className="mt-4 text-gray-600 animate-pulse">{message}</p>
    </div>
  );
};

export const LoadingButton = ({ children, loading, ...props }) => {
  return (
    <button {...props} disabled={loading || props.disabled}>
      {loading ? (
        <div className="flex items-center justify-center gap-2">
          <LoadingSpinner size="sm" />
          <span>Loading...</span>
        </div>
      ) : (
        children
      )}
    </button>
  );
};

export default LoadingSpinner; 